import { Segment } from "@heroui-pro/react/segment";
import { memo, useEffect, useRef, useState } from "react";
import type { Key } from "react-aria-components";
import { StepHeading } from "@funnel-ui/primitives/StepHeading";

type NPSStepProps = {
  question: string;
  description?: string;
  initialValue?: number;
  minLabel?: string;
  maxLabel?: string;
  autoAdvance?: boolean;
  onAnswer: (score: number) => void;
  className?: string;
  testId?: string;
};

const SCORES = Array.from({ length: 11 }, (_, i) => i); // 0..10
const AUTO_ADVANCE_DELAY_MS = 320;

const NPSStepImpl = ({
  question,
  description,
  initialValue,
  minLabel = "Not at all likely",
  maxLabel = "Extremely likely",
  autoAdvance = true,
  onAnswer,
  className,
  testId,
}: NPSStepProps) => {
  const [selectedKey, setSelectedKey] = useState<Key | undefined>(
    initialValue !== undefined ? String(initialValue) : undefined,
  );
  const advanceTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(
    () => () => {
      if (advanceTimer.current) clearTimeout(advanceTimer.current);
    },
    [],
  );

  const handleSelectionChange = (key: Key): void => {
    setSelectedKey(key);
    if (!autoAdvance) {
      onAnswer(Number(key));
      return;
    }
    if (advanceTimer.current) clearTimeout(advanceTimer.current);
    advanceTimer.current = setTimeout(() => onAnswer(Number(key)), AUTO_ADVANCE_DELAY_MS);
  };

  return (
    <section data-testid={testId} className={`flex flex-col gap-5 ${className ?? ""}`}>
      <StepHeading title={question} description={description} editablePath="question" />
      <Segment
        aria-label={question}
        size="sm"
        selectedKey={selectedKey ?? null}
        onSelectionChange={handleSelectionChange}
        className="w-full"
      >
        {SCORES.map((score) => (
          <Segment.Item key={score} id={String(score)} aria-label={String(score)}>
            {score}
          </Segment.Item>
        ))}
      </Segment>
      <div className="flex justify-between text-xs" style={{ color: "hsl(var(--hero-fg-muted))" }}>
        <span>{minLabel}</span>
        <span>{maxLabel}</span>
      </div>
    </section>
  );
};

export const NPSStep = memo(NPSStepImpl);

export default NPSStep;
